"use client";

import { useState } from "react";
import { Play, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { Link } from "@/components/common/Link";
type Recording = {
  id: number;
  title: string;
  location: string;
  category: string;
  timecode: string;
  coordinates: string;
  architecturalNote: string;
  fieldNotes: string;
  layout: string;
  image: string;
  srcset: string;
  sizes: string;
};
const recordings: Recording[] = [{
  id: 1,
  title: "Harry Chapman – Social Media Amplification & Audience Insight",
  location: "The Flavel Arts Centre, Dartmouth",
  category: "Social Media Amplification",
  timecode: "04:12",
  coordinates: "Dartmouth, Devon",
  architecturalNote: "Expanding social media outreach with graduate perspective on audience engagement.",
  fieldNotes: "Harry, a Marketing & Media graduate from Keele University, helped The Flavel expand social media activity, create digital content and provide insight into how younger audiences discover and engage with arts organisations. He contributed both marketing capability and a young person's perspective on audience development. Progression into freelance marketing work.",
  layout: "aspect-[16/10]",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706701/img/digital_transformation_project.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706701/img/digital_transformation_project.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706701/img/digital_transformation_project.png-wvc-sizes"
}, {
  id: 2,
  title: "Anna Scheer – ESG & Sustainability Framework",
  location: "The Flavel Arts Centre, Dartmouth",
  category: "ESG & Sustainability",
  timecode: "05:35",
  coordinates: "Dartmouth, Devon",
  architecturalNote: "Creating a practical sustainability framework to support strategy, governance and future grant funding.",
  fieldNotes: "The Flavel was already exploring sustainability initiatives including solar energy projects. Anna Scheer developed a Sustainability Framework that brought these activities together into a structured approach supporting governance, strategy and future grant funding applications. Anna holds a Biology degree from the University of Bristol and is completing an MSc in ESG Management at King's College London. Outcome: Practical ESG consultancy experience.",
  layout: "aspect-[16/10]",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706701/img/esg_sustainability_project.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706701/img/esg_sustainability_project.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706701/img/esg_sustainability_project.png-wvc-sizes"
}];
export default function Impact() {
  const [selected, setSelected] = useState<Recording | null>(null);
  return <section data-section-id="19274" id="impact-stories" className="bg-background text-foreground py-32 md:py-44">
      <div className="mx-auto max-w-[1440px] px-6 md:px-12">
        <div className="mx-auto max-w-3xl text-center">
          <p className="font-sans text-xs font-semibold tracking-[0.1em] uppercase text-chart-1">
            {"Evidence & Impact"}
          </p>
          <div className="mb-8">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706701/img/IMG_1562.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706701/img/IMG_1562.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706701/img/IMG_1562.png-wvc-sizes" className="mx-auto h-auto rounded-lg shadow-md !w-[100px]" alt="ESO Logo" loading="lazy" />
          </div>
          <h2 className="font-serif font-default mt-6 text-[clamp(2rem,4.2vw,4rem)] font-normal leading-[1.02] tracking-[-0.02em]">
            {"Case Studies"}
          </h2>
          <p className="mt-4 text-[clamp(1.0625rem,1.3vw,1.25rem)] leading-[1.55] text-muted-foreground">
            Real projects demonstrating how emerging talent can help organisations address capability gaps while gaining meaningful employability experience.
          </p>
        </div>

        <div className="mt-20 grid grid-cols-1 gap-8 mb-20">
          {recordings.map((item, i) => <Button key={item.id} data-index={i} onClick={() => setSelected(item)} variant="ghost" className={`group relative overflow-hidden bg-card text-left border border-border hover:border-chart-1 transition-colors duration-300 p-0 h-auto w-full ${item.layout}`}>
              <img src={item.image} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={`${item.title} — ${item.location}`} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]" />
              <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent" />

              <Badge variant="default" className="absolute left-4 top-4 z-10 bg-background/80 text-foreground backdrop-blur-sm border-transparent rounded-md font-mono text-xs tracking-[0.04em]">
                {item.category}
              </Badge>

              <div className="absolute bottom-4 right-4 z-10 flex h-10 w-10 items-center justify-center border border-chart-1 bg-background/50 backdrop-blur-sm opacity-0 translate-y-2 transition-all duration-[240ms] group-hover:opacity-100 group-hover:translate-y-0">
                <Play className="h-4 w-4 text-chart-1" />
              </div>

              <div className="absolute inset-x-0 bottom-0 z-10 p-6 md:p-8">
                <span className="block font-sans text-[10px] font-bold uppercase tracking-widest text-chart-1 mb-2">
                  Case Study
                </span>
                <h3 className="font-default font-sans text-[clamp(1.375rem,1.8vw,1.75rem)] font-medium leading-[1.2] tracking-[-0.01em] text-foreground">
                  {item.title}
                  <span className="block text-muted-foreground font-normal text-base mt-1">
                    {item.location}
                  </span>
                </h3>
              </div>
            </Button>)}
        </div>
      </div>

      <Dialog open={selected !== null} onOpenChange={open => !open && setSelected(null)}>
        <DialogContent showCloseButton={false} className="bg-card border border-border max-w-3xl p-0 overflow-hidden rounded-lg">
          {selected && <div className="flex flex-col h-full max-h-[90vh]">
              <div className="relative aspect-[16/9] shrink-0 overflow-hidden">
                <img src={selected.image} data-wvc-srcset={selected.srcset} data-wvc-sizes={selected.sizes} alt={selected.title} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
                <Button variant="ghost" size="icon" onClick={() => setSelected(null)} className="absolute right-3 top-3 h-9 w-9 bg-background/60 text-foreground hover:bg-background/80 backdrop-blur-sm rounded-md" aria-label="Close">
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex-1 overflow-y-auto p-8 md:p-10">
                <DialogTitle asChild>
                  <h3 className="font-default font-sans text-2xl font-medium tracking-[-0.01em]">
                    {selected.title}
                    <span className="block text-muted-foreground font-normal text-lg mt-1">
                      {selected.location}
                    </span>
                  </h3>
                </DialogTitle>

                <div className="mt-8 grid gap-6 md:grid-cols-2">
                  <div>
                    <p className="font-sans text-xs font-semibold tracking-[0.1em] uppercase text-muted-foreground">
                      Capability Area
                    </p>
                    <p className="mt-2 text-sm text-foreground">{selected.category}</p>
                  </div>
                  <div>
                    <p className="font-sans text-xs font-semibold tracking-[0.1em] uppercase text-muted-foreground">
                      Project Location
                    </p>
                    <p className="mt-2 text-sm text-foreground">
                      {selected.location}
                    </p>
                  </div>
                </div>

                <div className="mt-8 border-t border-border pt-6">
                  <p className="font-sans text-xs font-semibold tracking-[0.1em] uppercase text-muted-foreground">
                    Project Outcomes
                  </p>
                  <p className="mt-3 text-base leading-[1.65] text-foreground">
                    {selected.fieldNotes}
                  </p>
                  {selected.id === 1 && <video src="https://elevateso.org/wp-content/uploads/2026/07/WhatsApp-Video-2026-07-01-at-12.25.41.mp4" controls className="mt-6 w-full"></video>}
                  {selected.id === 2 && <div className="mt-4">
                      <Link to="https://images.savoysystems.co.uk/FLD/10453116.pdf" newTab className="inline-flex items-center justify-center h-11 px-6 bg-chart-1 text-white hover:bg-chart-1/90 rounded-lg font-medium text-sm transition-colors">
                        View Sustainability Framework
                      </Link>
                    </div>}
                </div>
              </div>

              <div className="p-6 border-t border-border bg-card shrink-0 flex flex-col sm:flex-row gap-3">
                <DialogClose asChild>
                  <Button variant="outline" className="h-12 px-8 border-border bg-transparent text-foreground hover:border-chart-1 hover:text-chart-1 rounded-lg font-medium tracking-[0.02em]">
                    Close
                  </Button>
                </DialogClose>
              </div>
            </div>}
        </DialogContent>
      </Dialog>
    </section>;
}